import { BaseApiClient } from "../api/baseApiClient.service";

/**
 * Podcast length enum (matches backend)
 */
export enum PodcastLength {
	SHORT = "SHORT",
	MEDIUM = "MEDIUM",
	LONG = "LONG",
}

/**
 * Document size categories
 */
export enum DocumentSize {
	SMALL = "SMALL", // 1-20 pages
	MEDIUM = "MEDIUM", // 21-50 pages
	LARGE = "LARGE", // 51-200 pages
	XLARGE = "XLARGE", // 201+ pages
}

/**
 * Coin pricing configuration from backend
 */
export interface CoinPricing {
	id: string;
	createdAt: string;
	updatedAt: string;
	shortChapterCost: number;
	mediumChapterCost: number;
	longChapterCost: number;
	smallDocMultiplier: number;
	mediumDocMultiplier: number;
	largeDocMultiplier: number;
	xlargeDocMultiplier: number;
	chatBasePerQuery: number;
	chatPerRetrievedChunk: number;
	isActive: boolean;
}

export interface PricingResponse {
	success: boolean;
	pricing: CoinPricing;
}

/**
 * V2 Cost estimate - simplified and user-friendly
 */
export interface CostEstimate {
	totalCost: number;
	costPerChapter: number;
	estimatedAudioMinutes: number;
	breakdown: {
		basePerChapter: number;
		chapterCount: number;
		documentSize: DocumentSize;
		sizeMultiplier: number;
		lengthDescription: string;
	};
	userContext?: {
		currentBalance: number;
		balanceAfter: number;
		canAfford: boolean;
		estimatedPodcastsRemaining: number;
		costPerMinute: number;
	};
}

export interface EstimateResponse {
	success: boolean;
	estimate: CostEstimate;
}

/** Rolling trial spend window (Creator trial only; optional on estimate APIs). */
export interface TrialRollingSnapshot {
	used: number;
	limit: number;
	/** Null when nothing was spent in the window, i.e. no countdown to show. */
	windowResetAt: string | null;
}

/** Response from GET podcast/estimate-from-data and GET podcast/estimate */
export interface PodcastCostEstimateApiResponse extends EstimateResponse {
	balance: number;
	hasSufficientBalance: boolean;
	trialRolling?: TrialRollingSnapshot;
	trialRollingBlocked?: boolean;
}

export interface BalanceResponse {
	success: boolean;
	balance: number;
	userId: string;
}

export interface Transaction {
	id: string;
	createdAt: string;
	type: string;
	status: string;
	amount: number;
	balanceBefore: number;
	balanceAfter: number;
	description: string | null;
	metadata: any;
	podcastId: string | null;
	podcastChapterId: string | null;
}

export interface TransactionResponse {
	success: boolean;
	transactions: Transaction[];
	pagination: {
		limit: number;
		offset: number;
		count: number;
	};
}

/**
 * CoinService V2: Simplified service for interacting with the coin/billing API
 *
 * Pricing Model: €1 per 45 minutes of audio
 * - 100 coins = €1
 * - 45 minutes = 100 coins
 * - 1 minute = ~2.22 coins
 */
export class CoinService {
	static path = "coin";

	/**
	 * Get current user's coin balance
	 */
	static async getBalance(): Promise<BalanceResponse> {
		return BaseApiClient.getInstance().get<BalanceResponse>(
			`${CoinService.path}/balance`,
		);
	}

	/**
	 * Get transaction history for current user
	 */
	static async getTransactions(params?: {
		limit?: number;
		offset?: number;
		type?: string;
	}): Promise<TransactionResponse> {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.set("limit", params.limit.toString());
		if (params?.offset) queryParams.set("offset", params.offset.toString());
		if (params?.type) queryParams.set("type", params.type);

		const query = queryParams.toString();
		return BaseApiClient.getInstance().get<TransactionResponse>(
			`${CoinService.path}/transactions${query ? `?${query}` : ""}`,
		);
	}

	/**
	 * Get cost estimate for podcast creation from extracted data
	 * Used when we have the extraction results
	 */
	static async estimatePodcastCostFromData(params: {
		pageCount: number;
		estimatedTextChars: number;
		chapterCount: number;
		podcastLength: PodcastLength;
	}): Promise<PodcastCostEstimateApiResponse> {
		const queryParams = new URLSearchParams({
			pageCount: params.pageCount.toString(),
			estimatedTextChars: params.estimatedTextChars.toString(),
			chapterCount: params.chapterCount.toString(),
			length: params.podcastLength,
		});

		return BaseApiClient.getInstance().get<PodcastCostEstimateApiResponse>(
			`podcast/estimate-from-data?${queryParams.toString()}`,
		);
	}

	/**
	 * Get cost estimate for podcast creation by workspace
	 */
	static async estimatePodcastCost(params: {
		podWorkspaceId: string;
		podcastLength: PodcastLength;
	}): Promise<PodcastCostEstimateApiResponse> {
		const queryParams = new URLSearchParams({
			podWorkspaceId: params.podWorkspaceId,
			length: params.podcastLength,
		});

		return BaseApiClient.getInstance().get<PodcastCostEstimateApiResponse>(
			`podcast/estimate?${queryParams.toString()}`,
		);
	}

	/**
	 * Get current pricing configuration
	 */
	static async getPricing(): Promise<PricingResponse> {
		return BaseApiClient.getInstance().get<PricingResponse>(
			`${CoinService.path}/pricing`,
		);
	}

	/**
	 * Helper: Format coins for display
	 * Shows both coins and approximate euro value
	 */
	static formatCoins(coins: number): string {
		const euros = (coins / 100).toFixed(2);
		return `${coins} coins (~€${euros})`;
	}

	/**
	 * Helper: Format audio minutes for display
	 */
	static formatAudioMinutes(minutes: number): string {
		if (minutes < 60) {
			return `${minutes} min`;
		}
		const hours = Math.floor(minutes / 60);
		const remainingMins = minutes % 60;
		return `${hours}h ${remainingMins}min`;
	}

	/**
	 * Helper: Get document size description
	 */
	static getDocumentSizeDescription(size: DocumentSize): string {
		const descriptions = {
			[DocumentSize.SMALL]: "Small (1-20 pages)",
			[DocumentSize.MEDIUM]: "Medium (21-50 pages)",
			[DocumentSize.LARGE]: "Large (51-200 pages)",
			[DocumentSize.XLARGE]: "Extra Large (201+ pages)",
		};
		return descriptions[size] || size;
	}

	/**
	 * Helper: Calculate cost per minute
	 */
	static calculateCostPerMinute(
		totalCost: number,
		audioMinutes: number,
	): number {
		if (audioMinutes === 0) return 0;
		return Number.parseFloat((totalCost / audioMinutes).toFixed(2));
	}
}
